import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Loader2 } from "lucide-react";
import { uploadReferralResume } from "@/api/storage";
import { addResumeToResumesArray } from "@/api/candidates";
import { AdminReferralWithUI } from "@/api/referrals";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { db } from "@/lib/firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";

interface CVUploadModalProps {
  open: boolean;
  onClose: () => void;
  referral: AdminReferralWithUI | null;
  onUploadSuccess?: () => void;
}

export function CVUploadModal({ open, onClose, referral, onUploadSuccess }: CVUploadModalProps) {
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type (PDF, DOC, DOCX)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF or Word document",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10MB",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
  }, [toast]);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleUpload = async () => {
    if (!selectedFile || !referral?.candidateData?.spannerUserId || !currentUser) {
      toast({
        title: "Error",
        description: "No file selected or missing candidate information",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsUploading(true);

      // Step 1: Upload the resume to Firebase Storage with proper metadata
      const uploadResult = await uploadReferralResume(
        selectedFile,
        referral.candidateEmail,
        referral.candidateData.spannerUserId,
        referral.jobId
      );

      // Step 2: Add the resume to the candidate's resumes array
      await addResumeToResumesArray(
        referral.candidateEmail,
        uploadResult.url,
        uploadResult.fileName,
        currentUser.uid // uploadedBy - the admin who uploaded it
      );

      // Step 3: Update the referral to mark it as no longer requiring resume upload
      // We update Firestore directly because requiresResumeUpload is on AdminReferral schema
      await updateDoc(doc(db, 'referrals', referral.id), {
        requiresResumeUpload: false,
        updatedAt: serverTimestamp()
      });

      toast({
        title: "Success",
        description: "CV uploaded and candidate profile updated successfully"
      });

      // Reset state
      setSelectedFile(null);
      
      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error uploading CV:", error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload CV",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="space-grotesk text-xl font-bold text-slate-800">Upload CV</DialogTitle>
          <DialogDescription className="space-grotesk text-slate-600">
            Upload a CV for {referral?.candidateName || 'this candidate'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Candidate Info */}
          <div className="rounded-lg bg-slate-50 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 space-grotesk">Candidate</span>
              <span className="text-sm text-slate-900 space-grotesk font-medium">{referral?.candidateName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 space-grotesk">Job</span>
              <span className="text-sm text-slate-900 space-grotesk font-medium">{referral?.jobTitle}</span>
            </div>
          </div>

          {/* CV Upload Section - Same UX as ReferralForm */}
          <div className="space-y-3">
            <div className="space-y-2">
              <h3 className="text-base font-bold text-slate-800 space-grotesk">CV Upload</h3>
              <p className="text-sm text-slate-600 space-grotesk">
                Upload the candidate's CV to complete their profile
              </p>
            </div>
            
            <div className="relative">
              {selectedFile ? (
                <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 space-grotesk">
                          {selectedFile.name}
                        </h4>
                        <p className="text-sm text-gray-500 space-grotesk">Ready to upload</p>
                      </div>
                    </div>
                    <button
                      onClick={handleRemoveFile}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      type="button"
                      disabled={isUploading}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="w-full bg-white border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-gray-400 transition-colors py-8 cursor-pointer"
                  onClick={triggerFileInput}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center mb-3">
                      <Upload className="w-5 h-5 text-gray-400" />
                    </div>
                    <h4 className="text-base font-medium text-gray-900 mb-1 space-grotesk">
                      Choose CV file
                    </h4>
                    <p className="text-sm text-gray-500 mb-3 space-grotesk">
                      PDF, DOC, or DOCX up to 10MB
                    </p>
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileSelect(file);
                          }
                        }}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={isUploading}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium disabled:opacity-50 cursor-pointer space-grotesk"
                      >
                        {isUploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" style={{ animationDuration: '1.5s' }}></div>
                            Uploading CV...
                          </>
                        ) : (
                          "Browse files"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isUploading}
              className="space-grotesk"
              size="md"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              className="space-grotesk"
              size="md"
            >
              {isUploading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" style={{ animationDuration: '1.2s' }}></div>
                  <span>Uploading...</span>
                </div>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload CV
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

